export const MISSION_LIGHTS_TOPIC = "__mission_lights__";

export const MISSION_LIGHT_TYPES = ["spotlight", "flash"] as const;

export type MissionLightType = (typeof MISSION_LIGHT_TYPES)[number];

export const MISSION_LIGHT_ACTION_TYPES = {
    SPOTLIGHT: "mission_spotlight_on",
    FLASH: "mission_flash_on",
    COMBINED: "mission_lights_on",
} as const;

export const MISSION_LIGHTS_RULE_ID = "mission-light:configured";

export type BehaviourMissionLightEntry = {
    enabled?: boolean;
};

export const isMissionLightsSource = (source: { kind?: string } | undefined) =>
    source?.kind === "mission_lights";

export const isMissionLightsCondition = (condition: { topic?: string } | undefined) =>
    condition?.topic === MISSION_LIGHTS_TOPIC;

export const isMissionLightsRule = (rule: {
    id?: string;
    isMissionLights?: boolean;
    conditions?: { items?: Array<{ topic?: string }> };
} | undefined) =>
    Boolean(
        rule?.isMissionLights ||
            String(rule?.id || "").startsWith("mission-light:") ||
            isMissionLightsCondition(rule?.conditions?.items?.[0]),
    );

export const lightTypeFromAction = (
    actionType: string | undefined,
): MissionLightType | null => {
    if (actionType === MISSION_LIGHT_ACTION_TYPES.SPOTLIGHT) return "spotlight";
    if (actionType === MISSION_LIGHT_ACTION_TYPES.FLASH) return "flash";
    return null;
};

export const actionTypeForLight = (lightType: MissionLightType) =>
    lightType === "flash"
        ? MISSION_LIGHT_ACTION_TYPES.FLASH
        : MISSION_LIGHT_ACTION_TYPES.SPOTLIGHT;

export const parseLightTypesParam = (raw: unknown): MissionLightType[] => {
    if (Array.isArray(raw)) {
        return raw.filter(
            (item): item is MissionLightType =>
                item === "spotlight" || item === "flash",
        );
    }
    if (typeof raw !== "string") return [];
    return raw
        .split(",")
        .map((item) => item.trim())
        .filter(
            (item): item is MissionLightType =>
                item === "spotlight" || item === "flash",
        );
};

export const lightTypesFromRule = (rule: {
    id?: string;
    lightType?: string;
    lightTypes?: string[];
    action?: { type?: string; params?: Record<string, string> };
} | undefined): MissionLightType[] => {
    if (!rule) return [];
    if (Array.isArray(rule.lightTypes) && rule.lightTypes.length > 0) {
        return rule.lightTypes.filter(
            (item): item is MissionLightType =>
                item === "spotlight" || item === "flash",
        );
    }
    const fromParams = parseLightTypesParam(rule.action?.params?.lights);
    if (fromParams.length > 0) return fromParams;
    if (rule.lightType === "spotlight" || rule.lightType === "flash") {
        return [rule.lightType];
    }
    const fromAction = lightTypeFromAction(rule.action?.type);
    if (fromAction) return [fromAction];
    const fromId = String(rule.id || "").split(":")[1];
    if (fromId === "spotlight" || fromId === "flash") return [fromId];
    return [];
};

/** @deprecated use lightTypesFromRule */
export const lightTypeFromRule = (
    rule: {
        id?: string;
        lightType?: string;
        lightTypes?: string[];
        action?: { type?: string; params?: Record<string, string> };
    },
): MissionLightType | null => lightTypesFromRule(rule)[0] ?? null;

export const entryFromStoredLight = (
    raw: BehaviourMissionLightEntry | boolean | undefined,
): BehaviourMissionLightEntry | null => {
    if (raw === undefined || raw === null) return null;
    if (raw === true) return {};
    if (raw === false) return { enabled: false };
    return raw;
};

export const isStoredLightEnabled = (
    raw: BehaviourMissionLightEntry | boolean | undefined,
) => {
    const entry = entryFromStoredLight(raw);
    if (!entry) return false;
    return entry.enabled !== false;
};

export const missionLightsToRule = (
    missionLights: Record<string, BehaviourMissionLightEntry | boolean | string> | undefined,
    catalogLightTypes: Array<{ light_type: string; label: string }> | undefined,
) => {
    const labels = new Map(
        (catalogLightTypes ?? []).map((item) => [item.light_type, item.label]),
    );
    const configured = MISSION_LIGHT_TYPES.filter(
        (lightType) => entryFromStoredLight(missionLights?.[lightType] as BehaviourMissionLightEntry | boolean | undefined) !== null,
    );
    if (configured.length === 0) return null;

    const enabled = configured.some((lightType) =>
        isStoredLightEnabled(missionLights?.[lightType] as BehaviourMissionLightEntry | boolean | undefined),
    );
    const labelList = configured
        .map((lightType) => labels.get(lightType) ?? lightType)
        .join(", ");
    const storedName =
        typeof missionLights?.name === "string" ? missionLights.name.trim() : "";

    return {
        id: MISSION_LIGHTS_RULE_ID,
        name: storedName || "Lights during mission",
        enabled,
        isMissionLights: true,
        lightTypes: configured,
        conditions: {
            logic: "and",
            items: [
                {
                    source: "mission_lights",
                    topic: MISSION_LIGHTS_TOPIC,
                    field: "mission_running",
                    operator: "eq",
                    value: "true",
                },
            ],
        },
        action: {
            type: MISSION_LIGHT_ACTION_TYPES.COMBINED,
            params: { lights: configured.join(",") },
        },
        _summaryLabels: labelList,
    };
};

export const buildMissionLightRules = (
    missionLights: Record<string, BehaviourMissionLightEntry | boolean | string> | undefined,
    catalogLightTypes: Array<{ light_type: string; label: string }> | undefined,
) => {
    const rule = missionLightsToRule(missionLights, catalogLightTypes);
    return rule ? [rule] : [];
};

export const missionLightsFromSelection = (
    selected: MissionLightType[],
    options: { enabled?: boolean; name?: string } = {},
): Record<string, BehaviourMissionLightEntry | string> => {
    const next: Record<string, BehaviourMissionLightEntry | string> = {};
    const name = options.name?.trim();
    if (name) {
        next.name = name;
    }
    for (const lightType of selected) {
        if (lightType !== "spotlight" && lightType !== "flash") continue;
        next[lightType] = options.enabled === false ? { enabled: false } : {};
    }
    return next;
};
